import { useCallback, useEffect, useRef, useState } from 'react'
import { Broom, Camera, CaretRight, CircleNotch, PaperPlaneRight, Microphone, ShoppingCart, Sparkle, X } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { usePricingContext } from '@/components/pricing/PricingContext'
import { resolveProductPricing } from '@/lib/pricing'
import { formatCommerceCurrency } from '@/lib/commerce-context'
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from '@/lib/product-image-placeholder'
import { aiSearchProducts, getProductsByIds, type AiSearchAnalyzingProduct } from '@/lib/api'
import {
  buildStorefrontSearchResultContext,
  buildStorefrontAiSearchAnalyticsPayload,
  buildStorefrontAiSearchFailureAnalyticsPayload,
  createStorefrontSearchId,
  hasStorefrontGranularAnalyticsConsent,
  queueStorefrontAnalyticsEvent,
  trackStorefrontSearchResultClick,
  type StorefrontSearchAnalyticsContext,
  type StorefrontSearchResultContext,
} from '@/lib/storefront-analytics'
import { useVoiceSearch } from '@/hooks/use-voice-search'
import { cn } from '@/lib/utils'
import type { Product } from '@/lib/types'

const AI_SEARCH_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const AI_SEARCH_IMAGE_MAX_BYTES = 10 * 1024 * 1024
const AI_SEARCH_IMAGE_MAX_EDGE = 1280
const AI_SEARCH_IMAGE_QUALITY = 0.82

/**
 * Reduz a foto no navegador (1280px JPEG) antes do envio: fotos de celular têm
 * vários MB e atravessam navegador → backend → gateway → API — comprimir aqui
 * corta o tempo de interpretação sem perder capacidade de identificação.
 */
const downscaleImageForAiSearch = async (
  file: File
): Promise<{ base64: string; previewUrl: string; mediaType: string } | null> => {
  let bitmap: ImageBitmap | null = null
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    try {
      bitmap = await createImageBitmap(file)
    } catch {
      bitmap = null
    }
  }
  if (!bitmap) return null
  try {
    const scale = Math.min(1, AI_SEARCH_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(bitmap, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg', AI_SEARCH_IMAGE_QUALITY)
    const base64 = dataUrl.split(',')[1] ?? ''
    if (!base64) return null
    return { base64, previewUrl: dataUrl, mediaType: 'image/jpeg' }
  } finally {
    bitmap.close()
  }
}

/** Card compacto (estilo marketplace) para os resultados da busca por IA. */
function AiSearchProductCard({
  product,
  onOpen,
  onAddToCart,
  searchResultContext,
}: {
  product: Product
  onOpen: (product: Product) => void
  onAddToCart: (product: Product, searchContext?: StorefrontSearchResultContext | null) => void
  searchResultContext?: StorefrontSearchResultContext | null
}) {
  const { t } = useTranslation()
  const pricingContext = usePricingContext()
  const pricing = resolveProductPricing(product, pricingContext)
  const imageUrl = String(product.image ?? '').trim() || PRODUCT_IMAGE_PLACEHOLDER_URL

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        trackStorefrontSearchResultClick(searchResultContext, 'open')
        onOpen(product)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          trackStorefrontSearchResultClick(searchResultContext, 'open')
          onOpen(product)
        }
      }}
      className="group flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-card p-2.5 transition-all hover:border-accent/50 hover:shadow-md"
    >
      <img
        src={imageUrl}
        alt={product.name}
        loading="lazy"
        className="h-16 w-16 shrink-0 rounded-lg border border-border/60 bg-white object-contain"
      />
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-sm leading-snug text-foreground">{product.name}</div>
        {pricing.canShowPrice && pricing.actualPrice !== null ? (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
            {pricing.hasCompareAtPrice && pricing.compareAtPrice !== null ? (
              <span className="text-xs text-muted-foreground line-through">
                {formatCommerceCurrency(pricing.compareAtPrice)}
              </span>
            ) : null}
            <span className="text-base font-bold text-primary">
              {formatCommerceCurrency(pricing.actualPrice)}
            </span>
            {pricing.discountPercent > 0 ? (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                {pricing.discountPercent}% OFF
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full text-muted-foreground hover:bg-accent/10 hover:text-accent"
          onClick={(event) => {
            event.stopPropagation()
            trackStorefrontSearchResultClick(searchResultContext, 'add_to_cart')
            onAddToCart(product, searchResultContext)
          }}
          aria-label={t('ai_search.add_to_cart')}
          title={t('ai_search.add_to_cart')}
        >
          <ShoppingCart className="h-4 w-4" />
        </Button>
        <CaretRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
      </div>
    </div>
  )
}

type AiSearchEntry = {
  id: string
  query: string
  imagePreview: string | null
  status: 'loading' | 'done' | 'error'
  // Progresso ao vivo (eventos SSE): etapa atual e miniaturas em análise.
  statusMessage: string | null
  analyzing: AiSearchAnalyzingProduct[]
  errorMessage: string | null
  observacao: string | null
  products: Product[]
  searchAnalyticsContext: StorefrontSearchAnalyticsContext | null
}

interface AiSearchPanelProps {
  open: boolean
  lojaId: number
  // Consulta vinda do dropdown de busca ("Usar busca por IA"): roda sozinha ao abrir.
  seed?: { query: string; key: number } | null
  onClose: () => void
  onProductClick: (product: Product) => void
  onAddToCart: (product: Product, searchContext?: StorefrontSearchResultContext | null) => void
}

/**
 * Busca por IA da loja: o cliente manda texto, foto ou voz; a IA devolve só os
 * códigos dos produtos e a própria vitrine monta os cards. Escopo exclusivo de
 * busca — cada envio é independente, sem conversa geral.
 */
export function AiSearchPanel({
  open,
  lojaId,
  seed = null,
  onClose,
  onProductClick,
  onAddToCart,
}: AiSearchPanelProps) {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<AiSearchEntry[]>([])
  const [text, setText] = useState('')
  const [pendingImage, setPendingImage] = useState<{
    mediaType: string
    base64: string
    name: string
    previewUrl: string
  } | null>(null)
  const [inputError, setInputError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const isSearching = entries.some((entry) => entry.status === 'loading')

  useEffect(() => {
    if (open) {
      window.setTimeout(() => textInputRef.current?.focus(), 80)
    }
  }, [open])

  // Rola só quando um novo envio entra (para mostrar a bolha do pedido);
  // quando os RESULTADOS chegam, a vista permanece no topo da lista — o
  // cliente lê do melhor resultado para baixo, sem pular para o fim.
  const entriesCountRef = useRef(0)
  useEffect(() => {
    const node = scrollRef.current
    if (node && entries.length > entriesCountRef.current) {
      node.scrollTop = node.scrollHeight
    }
    entriesCountRef.current = entries.length
  }, [entries])

  useEffect(() => () => {
    abortRef.current?.abort()
  }, [])

  const runSearch = useCallback(async (queryText: string) => {
    const message = queryText.trim()
    const image = pendingImage
    if ((!message && !image) || isSearching) return

    setText('')
    setPendingImage(null)
    setInputError(null)

    const entryId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const searchAnalyticsContext: StorefrontSearchAnalyticsContext | null = hasStorefrontGranularAnalyticsConsent()
      ? {
          searchId: createStorefrontSearchId(),
          searchMode: 'intelligent',
          searchTerm: message || 'Busca por imagem',
          sourceView: 'ai_search',
        }
      : null
    setEntries((prev) => [...prev, {
      id: entryId,
      query: message,
      imagePreview: image?.previewUrl ?? null,
      status: 'loading',
      statusMessage: null,
      analyzing: [],
      errorMessage: null,
      observacao: null,
      products: [],
      searchAnalyticsContext,
    }])

    const controller = new AbortController()
    abortRef.current = controller

    const patchEntry = (patch: Partial<AiSearchEntry>) => {
      setEntries((prev) => prev.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)))
    }

    let completedResultTracked = false
    try {
      const result = await aiSearchProducts({
        message,
        lojaId,
        attachment: image ? { mediaType: image.mediaType, base64: image.base64, name: image.name } : null,
        signal: controller.signal,
      }, {
        onStatus: (statusMessage) => patchEntry({ statusMessage }),
        onAnalyzing: (produtos) => patchEntry({ analyzing: produtos.slice(0, 3) }),
        onPartial: (produtoIds) => {
          // Mostra os candidatos já durante a validação; o resultado final
          // substitui (refina/reordena) quando chegar.
          void getProductsByIds(produtoIds).then((partialProducts) => {
            setEntries((prev) => prev.map((item) => (
              item.id === entryId && item.status === 'loading'
                ? { ...item, products: partialProducts.slice(0, 18) }
                : item
            )))
          }).catch(() => {})
        },
      })
      let products: Product[] = []
      try {
        products = result.produtoIds.length > 0
          ? await getProductsByIds(result.produtoIds)
          : []
      } finally {
        const displayedProducts = products.slice(0, 18)
        completedResultTracked = true
        try {
          queueStorefrontAnalyticsEvent({
            eventType: 'search',
            pageKind: 'search',
            pageKey: 'ai-search',
            payload: buildStorefrontAiSearchAnalyticsPayload({
              message,
              hasImage: Boolean(image),
              result,
              products: displayedProducts,
              searchId: searchAnalyticsContext?.searchId,
            }),
          })
        } catch (analyticsError) {
          console.warn('Falha ao registrar a busca inteligente no analytics.', analyticsError)
        }
      }
      patchEntry({
        status: 'done',
        observacao: result.observacao,
        products: products.slice(0, 18),
      })
    } catch (error) {
      if (controller.signal.aborted) return
      if (!completedResultTracked) {
        try {
          queueStorefrontAnalyticsEvent({
            eventType: 'search',
            pageKind: 'search',
            pageKey: 'ai-search',
            payload: buildStorefrontAiSearchFailureAnalyticsPayload({
              message,
              hasImage: Boolean(image),
              searchId: searchAnalyticsContext?.searchId,
            }),
          })
        } catch (analyticsError) {
          console.warn('Falha ao registrar o erro da busca inteligente no analytics.', analyticsError)
        }
      }
      patchEntry({
        status: 'error',
        errorMessage: error instanceof Error && error.message ? error.message : t('ai_search.error'),
      })
    }
  }, [isSearching, lojaId, pendingImage, t])

  const { isListening, isSupported: isVoiceSupported, toggleListening: toggleVoice } = useVoiceSearch((transcript) => {
    const value = transcript.trim()
    if (value) void runSearch(value)
  })

  const runSearchRef = useRef(runSearch)
  useEffect(() => {
    runSearchRef.current = runSearch
  }, [runSearch])

  const lastSeedKeyRef = useRef(0)
  useEffect(() => {
    if (!open || !seed || !seed.query || seed.key === lastSeedKeyRef.current) return
    lastSeedKeyRef.current = seed.key
    void runSearchRef.current(seed.query)
  }, [open, seed])

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!AI_SEARCH_IMAGE_TYPES.includes(String(file.type).toLowerCase())) {
      setInputError(t('ai_search.image_invalid'))
      return
    }
    if (file.size > AI_SEARCH_IMAGE_MAX_BYTES) {
      setInputError(t('ai_search.image_too_large'))
      return
    }
    const applyImage = (image: { base64: string; previewUrl: string; mediaType: string }) => {
      setInputError(null)
      setPendingImage({
        mediaType: image.mediaType,
        base64: image.base64,
        name: file.name || 'foto.jpg',
        previewUrl: image.previewUrl,
      })
      textInputRef.current?.focus()
    }
    void downscaleImageForAiSearch(file).then((downscaled) => {
      if (downscaled) {
        applyImage(downscaled)
        return
      }
      // Navegador sem suporte ao redimensionamento: envia o arquivo original.
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = String(reader.result ?? '')
        const base64 = dataUrl.split(',')[1] ?? ''
        if (!base64) {
          setInputError(t('ai_search.image_invalid'))
          return
        }
        applyImage({ base64, previewUrl: dataUrl, mediaType: String(file.type).toLowerCase() })
      }
      reader.onerror = () => setInputError(t('ai_search.image_invalid'))
      reader.readAsDataURL(file)
    })
  }, [t])

  const handleProductOpen = useCallback((product: Product) => {
    onClose()
    onProductClick(product)
  }, [onClose, onProductClick])

  if (!open) return null

  // Dropdown ancorado na barra de busca (o pai é o container relativo do
  // search): mantém o cliente no contexto da página, sem popup de chat.
  return (
    <div
      className="absolute top-full left-0 right-0 z-50 mt-2 animate-in fade-in slide-in-from-top-2 duration-200"
      role="region"
      aria-label={t('ai_search.title')}
    >
      <style>{`
        @keyframes ai-scan-sweep {
          0% { top: -8%; }
          100% { top: 104%; }
        }
        .ai-scan-line {
          position: absolute;
          left: -4%;
          right: -4%;
          height: 3px;
          border-radius: 9999px;
          background: linear-gradient(90deg, transparent, var(--accent), transparent);
          box-shadow: 0 0 14px 4px color-mix(in oklab, var(--accent) 55%, transparent);
          animation: ai-scan-sweep 1.5s ease-in-out infinite alternate;
        }
        .ai-scan-tint {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, color-mix(in oklab, var(--accent) 10%, transparent), transparent 35%, transparent 65%, color-mix(in oklab, var(--accent) 10%, transparent));
          animation: ai-scan-pulse 1.5s ease-in-out infinite alternate;
        }
        @keyframes ai-scan-pulse {
          0% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
      <div className="flex max-h-[min(70vh,640px)] w-full flex-col overflow-hidden rounded-2xl border border-accent/20 bg-background shadow-2xl">
        {/* Cabeçalho neutro — sem persona, o destaque fica só no ícone */}
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-background px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Sparkle className="h-5 w-5 text-accent" weight="fill" />
            <div>
              <div className="text-sm font-semibold leading-4 text-foreground">{t('ai_search.title')}</div>
              <div className="text-[11px] text-muted-foreground">{t('ai_search.subtitle')}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {entries.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => {
                  abortRef.current?.abort()
                  setEntries([])
                  setPendingImage(null)
                  setInputError(null)
                  setText('')
                  textInputRef.current?.focus()
                }}
                aria-label={t('ai_search.clear')}
                title={t('ai_search.clear')}
              >
                <Broom className="h-4 w-4" />
                {t('ai_search.clear')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={onClose}
              aria-label={t('ai_search.close')}
            >
              <X className="h-4 w-4" weight="bold" />
            </Button>
          </div>
        </div>

        {/* Conteúdo */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
              {t('ai_search.intro')}
            </div>
          ) : null}

          {entries.map((entry) => (
            <div key={entry.id} className="space-y-3">
              {entry.imagePreview && entry.status === 'loading' ? (
                /* Enquanto busca: foto centralizada, maior, com efeito de
                   escaneamento — distrai o cliente durante a espera. */
                <div className="flex flex-col items-center gap-2">
                  <div className="ai-scan-frame relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                    <img
                      src={entry.imagePreview}
                      alt=""
                      className="max-h-72 w-auto max-w-full object-contain"
                    />
                    <span aria-hidden="true" className="ai-scan-tint" />
                    <span aria-hidden="true" className="ai-scan-line" />
                  </div>
                  {entry.query ? (
                    <span className="max-w-[85%] text-center text-sm text-muted-foreground">{entry.query}</span>
                  ) : null}
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="max-w-[75%] rounded-2xl rounded-br-md bg-muted px-3 py-1.5 text-sm text-foreground">
                    {entry.imagePreview ? (
                      <img
                        src={entry.imagePreview}
                        alt=""
                        className="my-1 max-h-28 rounded-lg object-contain"
                      />
                    ) : null}
                    {entry.query ? <span>{entry.query}</span> : null}
                  </div>
                </div>
              )}

              {entry.status === 'loading' ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CircleNotch className="h-4 w-4 shrink-0 animate-spin" weight="bold" />
                  {entry.analyzing.some((item) => item.imagem) ? (
                    <span className="flex shrink-0 items-center -space-x-1.5">
                      {entry.analyzing.filter((item) => item.imagem).slice(0, 3).map((item) => (
                        <img
                          key={item.id}
                          src={item.imagem ?? undefined}
                          alt={item.nome}
                          title={item.nome}
                          className="h-6 w-6 rounded-full border-2 border-background bg-white object-cover"
                        />
                      ))}
                    </span>
                  ) : null}
                  <span className="min-w-0 truncate">{entry.statusMessage || t('ai_search.searching')}</span>
                </div>
              ) : entry.status === 'error' ? (
                <div className="rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {entry.errorMessage || t('ai_search.error')}
                </div>
              ) : entry.products.length === 0 ? (
                <div className="rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {entry.observacao || t('ai_search.empty')}
                </div>
              ) : null}
              {entry.products.length > 0 ? (
                <div className="space-y-2">
                  {entry.status === 'done' ? (
                    <div className="text-xs text-muted-foreground">
                      {entry.observacao || t('ai_search.results')}
                    </div>
                  ) : null}
                  <div className={cn('grid grid-cols-1 gap-2', entry.status === 'loading' && 'opacity-75')}>
                    {entry.products.map((product, index) => {
                      const searchResultContext = entry.searchAnalyticsContext
                        ? buildStorefrontSearchResultContext(entry.searchAnalyticsContext, product, {
                            position: index + 1,
                            page: 1,
                          })
                        : null

                      return (
                        <AiSearchProductCard
                          key={product.id}
                          product={product}
                          onOpen={handleProductOpen}
                          onAddToCart={onAddToCart}
                          searchResultContext={searchResultContext}
                        />
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Entrada */}
        <div className="shrink-0 border-t border-border bg-background px-3 py-3">
          {pendingImage ? (
            <div className="mb-2 flex items-center gap-2">
              <div className="relative">
                <img src={pendingImage.previewUrl} alt="" className="h-12 w-12 rounded-lg border border-border object-cover" />
                <button
                  type="button"
                  onClick={() => setPendingImage(null)}
                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-foreground/70 text-background"
                  aria-label={t('ai_search.remove_image')}
                >
                  <X className="h-2.5 w-2.5" weight="bold" />
                </button>
              </div>
              <span className="truncate text-xs text-muted-foreground">{pendingImage.name}</span>
            </div>
          ) : null}
          {inputError ? (
            <div className="mb-2 text-xs text-destructive">{inputError}</div>
          ) : null}
          <div className="flex items-center gap-1.5">
            <input
              ref={fileInputRef}
              type="file"
              accept={AI_SEARCH_IMAGE_TYPES.join(',')}
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSearching}
              aria-label={t('ai_search.attach')}
              title={t('ai_search.attach')}
            >
              <Camera className="h-5 w-5" />
            </Button>
            <input
              ref={textInputRef}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void runSearch(text)
                }
              }}
              placeholder={isListening ? t('ai_search.listening') : t('ai_search.placeholder')}
              className={cn(
                'h-10 min-w-0 flex-1 rounded-full border border-border bg-muted/40 px-4 text-sm text-foreground outline-none focus:border-accent',
                isListening && 'border-destructive/60'
              )}
              disabled={isSearching}
            />
            {isVoiceSupported ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'h-10 w-10 shrink-0 rounded-full text-muted-foreground hover:text-foreground',
                  isListening && 'bg-destructive/10 text-destructive hover:text-destructive'
                )}
                onClick={toggleVoice}
                disabled={isSearching}
                aria-label={t('ai_search.voice')}
                title={t('ai_search.voice')}
              >
                <Microphone className="h-5 w-5" weight={isListening ? 'fill' : 'regular'} />
              </Button>
            ) : null}
            <Button
              type="button"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => void runSearch(text)}
              disabled={isSearching || (!text.trim() && !pendingImage)}
              aria-label={t('ai_search.send')}
              title={t('ai_search.send')}
            >
              {isSearching ? (
                <CircleNotch className="h-5 w-5 animate-spin" weight="bold" />
              ) : (
                <PaperPlaneRight className="h-5 w-5" weight="fill" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
